import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Stack,
  Group,
  Box,
  Select,
  TextInput,
  Textarea,
  NumberInput,
  Switch,
  Text,
  Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { MedicationStatement, CodeableConcept } from '@medplum/fhirtypes';
import { CodeableConceptInput } from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import FormActionButtons, { FormActionButtonsRef } from '@/components/FormActionButtons';
import { isEqual } from 'lodash';
import { ARKON } from '@consts/systems.consts';
import {
  getDoseValue,
  getDoseUnit,
  getFrequency,
  getPeriod,
  getPeriodUnit,
  getRoute,
  getDosageText,
  getAsNeeded,
  getNoteText,
  getStatusReason,
  parseEffectiveStart,
  parseEffectiveEnd,
  setMedicationCodeableConcept,
  setStatus,
  setDoseQuantity,
  setTiming,
  setRoute,
  setDosageText,
  setAsNeeded,
  setNote,
  setStatusReason,
  setEffectivePeriod,
  setDateAsserted,
  hasMedicationName,
} from '@/utils/medication.utils';
import { STATUS_OPTIONS, PERIOD_UNIT_OPTIONS, COMMON_DOSE_UNITS } from '@consts/medication.consts';

export interface MedicationModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (medication: MedicationStatement) => void;
  /** Function to save medication to server. If provided, modal handles async save. */
  onSaveToServer?: (medication: MedicationStatement) => Promise<MedicationStatement>;
  medication?: MedicationStatement;
  mode: 'add' | 'edit';
}

// Default empty medication for new entries
const createEmptyMedication = (): MedicationStatement => ({
  resourceType: 'MedicationStatement',
  status: 'active',
  subject: { reference: '' }, // Will be set by caller
});

export const MedicationModal = ({
  opened,
  onClose,
  onSave,
  onSaveToServer,
  medication,
  mode,
}: MedicationModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Form state - using MedicationStatement directly
  const [formData, setFormData] = useState<MedicationStatement>(createEmptyMedication);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Key to force CodeableConceptInput remount on each modal open
  const [inputKey, setInputKey] = useState(0);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<MedicationStatement | null>(null);
  const baselineDatesRef = useRef<{ onset: Date | null; end: Date | null } | null>(null);
  const [baselineSet, setBaselineSet] = useState(false);

  // Date state (separate because FHIR uses strings, form uses Date)
  const [onsetDate, setOnsetDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Reset form when modal opens or medication changes
  useEffect(() => {
    if (opened) {
      // Increment key to force CodeableConceptInput to remount with fresh defaultValue
      setInputKey((prev) => prev + 1);
      setValidationError(null);
      setBaselineSet(true); // Flag to capture baseline after state is set
      if (medication) {
        setFormData({ ...medication });
        setOnsetDate(parseEffectiveStart(medication));
        setEndDate(parseEffectiveEnd(medication));
      } else {
        setFormData({
          ...createEmptyMedication(),
          dateAsserted: new Date().toISOString().split('T')[0],
        });
        setOnsetDate(new Date());
        setEndDate(null);
      }
    } else {
      setBaselineSet(false);
    }
  }, [opened, medication]);

  // Capture baseline after form state is initialized
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { ...formData };
      baselineDatesRef.current = { onset: onsetDate, end: endDate };
      setBaselineSet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, onsetDate, endDate]);

  // Update formData when dates change
  const updateDates = useCallback((start: Date | null, end: Date | null) => {
    setFormData((prev) => setEffectivePeriod(prev, start, end));
  }, []);

  useEffect(() => {
    updateDates(onsetDate, endDate);
  }, [onsetDate, endDate, updateDates]);

  // Clear validation error on any change
  const clearValidation = () => {
    if (validationError) {
      setValidationError(null);
    }
  };

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }
    
    try {
      const formChanged = !isEqual(formData, baselineRef.current);
      const datesChanged = !isEqual(
        { onset: onsetDate, end: endDate },
        baselineDatesRef.current
      );
      return formChanged || datesChanged;
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormData({ ...baselineRef.current });
    }
    if (baselineDatesRef.current) {
      setOnsetDate(baselineDatesRef.current.onset);
      setEndDate(baselineDatesRef.current.end);
    }
    setInputKey((prev) => prev + 1); // Reset CodeableConceptInput
    setValidationError(null);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!hasMedicationName(formData)) {
      setValidationError(
        t('medications.modal.validationMedicationRequired', {
          defaultValue: 'Please select or enter a medication name',
        })
      );
      return;
    }
    if (!formData.status) {
      setValidationError(
        t('medications.modal.validationStatusRequired', {
          defaultValue: 'Please select a status',
        })
      );
      return;
    }

    // Ensure dateAsserted is set
    let dataToSave = formData;
    if (!dataToSave.dateAsserted) {
      dataToSave = setDateAsserted(dataToSave, new Date());
    }

    // If onSaveToServer is provided, save to server immediately
    if (onSaveToServer) {
      setSaving(true);
      try {
        const serverSaved = await onSaveToServer(dataToSave);
        onSave(serverSaved);
        onClose();
      } catch (error) {
        notifications.show({
          color: 'red',
          title: t('medications.modal.saveErrorTitle', {
            defaultValue: 'Failed to save medication',
          }),
          message: String(error),
        });
      } finally {
        setSaving(false);
      }
    } else {
      // Fallback to local-only save (no server call)
      onSave(dataToSave);
      onClose();
    }
  };

  const requiresEndDate = ['stopped', 'completed'].includes(formData.status || '');

  // Extract current values using getters
  const currentDoseValue = getDoseValue(formData);
  const currentDoseUnit = getDoseUnit(formData);
  const currentFrequency = getFrequency(formData);
  const currentPeriod = getPeriod(formData);
  const currentPeriodUnit = getPeriodUnit(formData);
  const currentRoute = getRoute(formData);
  const currentDosageText = getDosageText(formData);
  const currentAsNeeded = getAsNeeded(formData);
  const currentNote = getNoteText(formData);
  const currentStatusReason = getStatusReason(formData);

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={
        mode === 'add'
          ? t('medications.modal.addTitle')
          : t('medications.modal.editTitle')
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Validation Error */}
        {validationError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {validationError}
          </Alert>
        )}

        {/* Medication Name */}
        <CodeableConceptInput
          key={`medication-${inputKey}`}
          path="MedicationStatement.medicationCodeableConcept"
          binding={ARKON.VS.MEDICATIONS_EL}
          label={t('medications.medicationName')}
          name="medication-name"
          maxValues={1}
          defaultValue={formData.medicationCodeableConcept}
          onChange={(code) => {
            setFormData((prev) => setMedicationCodeableConcept(prev, code));
            clearValidation();
          }}
          outcome={undefined}
          required
        />

        {/* Status */}
        <Select
          label={t('medications.status')}
          data={STATUS_OPTIONS.map((status) => ({
            value: status,
            label: t(`medications.statusOptions.${status}` as never),
          }))}
          value={formData.status || null}
          onChange={(value) => {
            setFormData((prev) => setStatus(prev, value as MedicationStatement['status']));
            clearValidation();
          }}
          required
        />

        {/* Dates */}
        <Group grow>
          <DateInputWrapper
            name="onsetDate"
            label={t('medications.onsetDate')}
            value={onsetDate}
            onChange={(date) => {
              setOnsetDate(date);
              clearValidation();
            }}
            required={requiresEndDate}
          />
          {requiresEndDate && (
            <DateInputWrapper
              name="endDate"
              label={t('medications.endDate')}
              value={endDate}
              onChange={(date) => {
                setEndDate(date);
                clearValidation();
              }}
              required
            />
          )}
        </Group>

        {/* Dosage Section */}
        <Text fw={600} size="sm" mt="md">
          {t('medications.dosageSection')}
        </Text>

        {/* Dose Quantity */}
        <Group grow>
          <NumberInput
            label={t('medications.doseValue')}
            value={currentDoseValue ?? ''}
            onChange={(value) => {
              const numValue = typeof value === 'number' ? value : undefined;
              setFormData((prev) => setDoseQuantity(prev, numValue, currentDoseUnit));
              clearValidation();
            }}
            min={0}
            decimalScale={2}
          />
          <Select
            label={t('medications.doseUnit')}
            data={COMMON_DOSE_UNITS.map((opt) => ({
              value: opt.value,
              label: t(opt.label as never),
            }))}
            value={currentDoseUnit || null}
            onChange={(value) => {
              setFormData((prev) => setDoseQuantity(prev, currentDoseValue, value || undefined));
              clearValidation();
            }}
            searchable
            clearable
          />
        </Group>

        {/* Route */}
        <CodeableConceptInput
          key={`route-${inputKey}`}
          path="MedicationStatement.dosage.route"
          binding={ARKON.VS.MEDICATION_ROUTE_EL_EN}
          label={t('medications.route')}
          name="route"
          maxValues={1}
          defaultValue={currentRoute}
          onChange={(code) => {
            setFormData((prev) => setRoute(prev, code as CodeableConcept | undefined));
            clearValidation();
          }}
          outcome={undefined}
        />

        {/* Frequency/Timing */}
        <Text size="sm" c="dimmed">
          {t('medications.frequencyLabel')}
        </Text>
        <Group grow>
          <NumberInput
            label={t('medications.frequencyTimes')}
            value={currentFrequency ?? ''}
            onChange={(value) => {
              const numValue = typeof value === 'number' ? value : undefined;
              setFormData((prev) =>
                setTiming(prev, numValue, currentPeriod, currentPeriodUnit as 'd' | 'wk' | 'mo' | undefined)
              );
              clearValidation();
            }}
            min={1}
            placeholder="e.g., 2"
          />
          <NumberInput
            label={t('medications.periodValue')}
            value={currentPeriod ?? ''}
            onChange={(value) => {
              const numValue = typeof value === 'number' ? value : undefined;
              setFormData((prev) =>
                setTiming(prev, currentFrequency, numValue, currentPeriodUnit as 'd' | 'wk' | 'mo' | undefined)
              );
              clearValidation();
            }}
            min={1}
            placeholder="e.g., 1"
          />
          <Select
            label={t('medications.periodUnit')}
            data={PERIOD_UNIT_OPTIONS.map((opt) => ({
              value: opt.value,
              label: t(opt.label as never),
            }))}
            value={currentPeriodUnit || null}
            onChange={(value) => {
              setFormData((prev) =>
                setTiming(prev, currentFrequency, currentPeriod, value as 'd' | 'wk' | 'mo' | undefined)
              );
              clearValidation();
            }}
            clearable
          />
        </Group>

        {/* As Needed (PRN) */}
        <Box w="fit-content">
          <Switch
            label={t('medications.asNeededLabel')}
            description={t('medications.asNeededDescription')}
            checked={currentAsNeeded || false}
            onChange={(event) => {
              setFormData((prev) => setAsNeeded(prev, event.currentTarget.checked));
              clearValidation();
            }}
          />
        </Box>

        {/* Free-text dosage instructions */}
        <Textarea
          label={t('medications.dosageText')}
          placeholder={t('medications.dosageTextPlaceholder')}
          value={currentDosageText || ''}
          onChange={(e) => {
            setFormData((prev) => setDosageText(prev, e.currentTarget.value || undefined));
            clearValidation();
          }}
          rows={2}
        />

        {/* Notes */}
        <Textarea
          label={t('medications.notes')}
          placeholder={t('medications.notesPlaceholder')}
          value={currentNote || ''}
          onChange={(e) => {
            setFormData((prev) => setNote(prev, e.currentTarget.value || undefined));
            clearValidation();
          }}
          rows={2}
        />

        {/* Stop Reason (only shown for stopped/completed) */}
        {requiresEndDate && (
          <TextInput
            label={t('medications.stopReason')}
            placeholder={t('medications.stopReasonPlaceholder')}
            value={currentStatusReason?.text || ''}
            onChange={(e) => {
              setFormData((prev) => setStatusReason(prev, e.currentTarget.value || undefined));
              clearValidation();
            }}
          />
        )}

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          isLoading={saving}
          saveLabel={
            mode === 'add'
              ? t('medications.modal.addButton')
              : t('medications.modal.saveButton')
          }
        />
      </Stack>
    </Modal>
  );
};
